import { Alert, Box, CircularProgress, Link, TextField } from "@mui/material";
import { type ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { SubmitButton } from "~/components/Buttons";
import { BodyText, BodyTextSmall } from "~/components/Typographies";
import MultiFactorQrCode from "~settings/security/components/MultiFactorQrCode";
import MultiFactorRecoveryCodesList from "~settings/security/components/MultiFactorRecoveryCodesList";
import MultiFactorSecretReveal from "~settings/security/components/MultiFactorSecretReveal";
import { isCompleteAuthenticatorCode } from "~settings/security/helpers/multiFactorAuth";
import { type UseMultiFactorEnrollFlowReturn } from "~settings/security/hooks/useMultiFactorEnrollFlow";

interface MultiFactorEnrollContentProps extends UseMultiFactorEnrollFlowReturn {
	onDone: () => void;
}

const MultiFactorEnrollContent = ({
	code,
	codeError,
	enrollment,
	isCompleting,
	onDone,
	recoveryCodes,
	retryStart,
	setCode,
	startError,
	step,
	submitCode
}: MultiFactorEnrollContentProps) => {
	const { t } = useTranslation("common");
	const [manualEntryShown, setManualEntryShown] = useState(false);

	if (step === "starting") {
		if (startError) {
			return (
				<Box sx={{ alignItems: "center", display: "flex", flexDirection: "column", gap: 2, py: 4 }}>
					<BodyText color="error">{startError}</BodyText>
					<SubmitButton fullWidth={false} onClick={retryStart}>
						{t("preferences.security.enroll.retryButton")}
					</SubmitButton>
				</Box>
			);
		}
		return (
			<Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", py: 6 }}>
				<CircularProgress aria-label={t("preferences.security.enroll.title")} />
			</Box>
		);
	}

	if (step === "enterCode" && enrollment) {
		const isReady = isCompleteAuthenticatorCode(code, enrollment.digits);
		const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => setCode(event.target.value);

		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<BodyTextSmall color="text.secondary">{t("preferences.security.enroll.scanInstructions")}</BodyTextSmall>
				<Box sx={{ display: "flex", justifyContent: "center" }}>
					<MultiFactorQrCode otpAuthUri={enrollment.otpAuthUri} />
				</Box>
				<Link component="button" onClick={() => setManualEntryShown((shown) => !shown)} type="button">
					{t("preferences.security.enroll.manualEntryToggle")}
				</Link>
				{manualEntryShown && <MultiFactorSecretReveal secretBase32Encoded={enrollment.secretBase32Encoded} />}
				<Box sx={{ alignItems: "flex-start", display: "flex", flexWrap: "wrap", gap: 1 }}>
					<TextField
						error={!!codeError}
						helperText={codeError ?? ""}
						label={t("preferences.security.enroll.codeLabel")}
						onChange={handleCodeChange}
						slotProps={{
							htmlInput: {
								"aria-label": t("preferences.security.enroll.codeAria"),
								inputMode: "numeric",
								maxLength: enrollment.digits
							}
						}}
						value={code}
					/>
					<SubmitButton disabled={isCompleting || !isReady} fullWidth={false} onClick={() => submitCode()}>
						{isCompleting
							? t("preferences.security.enroll.verifyingButton")
							: t("preferences.security.enroll.verifyButton")}
					</SubmitButton>
				</Box>
			</Box>
		);
	}

	if (step === "recoveryCodes") {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<Alert severity="success">{t("preferences.security.enroll.successTitle")}</Alert>
				<BodyTextSmall color="text.secondary">{t("preferences.security.enroll.successDescription")}</BodyTextSmall>
				<MultiFactorRecoveryCodesList codes={recoveryCodes} />
				<SubmitButton onClick={onDone}>{t("preferences.security.enroll.doneButton")}</SubmitButton>
			</Box>
		);
	}

	return null;
};

export default MultiFactorEnrollContent;
